// Turns an export spec into a file download in the browser.
// spec: { filename (no extension), headers, rows, widths, title, subtitle }
// `rows` is an array of cell arrays; a null entry is a blank spacer row.
// exceljs and jspdf are imported on demand so the admin bundle stays small.

export const EXPORT_TYPES = [
  { key: 'xlsx', label: 'Excel (.xlsx)' },
  { key: 'csv',  label: 'CSV' },
  { key: 'pdf',  label: 'PDF' },
];

const FONT = { name: 'Calibri', size: 12 };

function save(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function toXlsx({ headers, rows, widths = {} }) {
  const mod     = await import('exceljs');
  const ExcelJS = mod.default?.Workbook ? mod.default : mod;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sheet1');
  ws.addRow(headers).font = { ...FONT, bold: true };
  rows.forEach(r => { ws.addRow(r || []).font = FONT; });
  Object.entries(widths).forEach(([col, w]) => { ws.getColumn(col).width = w; });
  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function toCsv({ headers, rows }) {
  const cell = v => {
    const s = v == null ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers, ...rows.map(r => r || [])].map(r => r.map(cell).join(','));
  // BOM so Excel opens accented names correctly
  return new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
}

async function toPdf({ headers, rows, title, subtitle }) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  // Drop columns that are empty all the way down (the Aisle Planner layout is mostly blanks)
  const keep = headers.map((_, i) => rows.some(r => r && r[i] != null && r[i] !== ''));
  const head = headers.filter((_, i) => keep[i]);
  const body = rows.map(r => (r || []).filter((_, i) => keep[i]));

  const doc = new jsPDF({ orientation: head.length > 5 ? 'landscape' : 'portrait', unit: 'pt', format: 'letter' });
  doc.setFont('helvetica', 'bold').setFontSize(15).setTextColor(31, 41, 55).text(title, 40, 44);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(107, 114, 128).text(subtitle, 40, 60);
  autoTable(doc, {
    head: [head], body, startY: 76, margin: { left: 40, right: 40 },
    styles:    { font: 'helvetica', fontSize: 8.5, cellPadding: 5, textColor: [55, 65, 81], lineColor: [243, 244, 246], lineWidth: 0.5 },
    headStyles: { fillColor: [249, 250, 251], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: false },
    didDrawPage: ({ pageNumber }) => {
      const { width, height } = doc.internal.pageSize;
      doc.setFontSize(8).setTextColor(156, 163, 175).text(`Page ${pageNumber}`, width - 40, height - 24, { align: 'right' });
    },
  });
  return doc.output('blob');
}

export default async function downloadExport(type, spec) {
  const blob = type === 'xlsx' ? await toXlsx(spec) : type === 'csv' ? toCsv(spec) : await toPdf(spec);
  save(blob, `${spec.filename}.${type}`);
}
